"use client";

import { useContext, useState } from "react";
import { FiAlignCenter } from "react-icons/fi";
import Link from "next/link";
import { IoMoonOutline, IoSunnyOutline } from "react-icons/io5";
import { BiSearch } from "react-icons/bi";
import { MdMenu } from "react-icons/md";

import HeaderLogo from "./HeaderLogo";
import { ArticleCategoryType } from "@/types/article";
import { SocialLinkType } from "@/types/common";
import { StaticAdPageDataType } from "@/services/adServices";
import HamburgerMenuModal from "./HamburgerMenuModal";
import UserDropdown from "./UserDropdown";
import { AuthContext } from "@/components/providers/AuthContextProvider";
import { useTheme } from "@/components/providers/ThemeProvider";
import SearchModal from "./SearchModal";
import AdvertisementBlock from "@/components/blocks/AdvertisementBlock";

interface MainHeaderSectionProps {
  mainNavLinks: ArticleCategoryType[];
  categories: ArticleCategoryType[];
  logoUrl?: string;
  darkLogoUrl?: string;
  socialLinks: SocialLinkType[];
  lang: string;
  advertisement: StaticAdPageDataType;
}

export default function MainHeaderSection({
  mainNavLinks,
  categories,
  logoUrl,
  darkLogoUrl,
  socialLinks,
  lang,
  advertisement,
}: MainHeaderSectionProps) {
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [hamburgerMenuOpen, setHamburgerMenuOpen] = useState(false);
  const advertisementData = advertisement?.data?.adBlocks?.find(item => {
    return item?.adName === "topHeaderAd";
  });
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const { darkMode, toggleDarkMode } = useTheme();
  const authContext = useContext(AuthContext);

  if (!authContext) {
    return null;
  }

  const { user } = authContext;

  return (
    <>
      {(!!advertisementData?.adImage || !!advertisementData?.adCode) && (
        <div className="mx-auto w-full max-w-[750px] h-auto rounded container py-4">
          <AdvertisementBlock
            block={{
              id: "",
              adType: "horizontal",
              blockType: "",
              adName: "topHeaderAd",
            }}
            advertisementData={advertisementData}
          />
        </div>
      )}

      <div className="sticky top-0 z-20 bg-white dark:bg-darkBgPrimaryColor ">
        <div className="container">
          <div className="flex items-center self-stretch justify-between py-3">
            {/* Logo Section */}
            <div onClick={() => setHamburgerMenuOpen(true)} className="lg:hidden dark:text-white">
              <FiAlignCenter size={30} />
            </div>
            <div className="flex flex-col items-center gap-2">
              <HeaderLogo logoUrl={logoUrl} darkLogoUrl={darkLogoUrl} />
            </div>

            <div className="flex items-center gap-5">
              <button
                onClick={toggleDarkMode}
                className={`hidden lg:flex w-12 h-6 items-center rounded-full p-1 transition-colors duration-300 ${
                  darkMode ? "bg-gray-400" : "bg-gray-300"
                }`}
              >
                <div
                  className={`w-4 h-4 rounded-full flex items-center justify-center shadow-md transform transition-transform duration-300 text-xs ${
                    darkMode
                      ? "translate-x-6 bg-gray-600 text-white"
                      : "translate-x-0 bg-[#5F5F5F] text-white "
                  }`}
                >
                  {darkMode ? <IoMoonOutline /> : <IoSunnyOutline />}
                </div>
              </button>

              <div
                onClick={() => setSearchModalOpen(true)}
                className="dark:text-white text-[27px] lg:text-xl"
              >
                <BiSearch />
              </div>

              <div className="hidden lg:flex items-center gap-2">
                <a href="/epaper" target="_blank" aria-label="Open the E-paper in a new tab">
                  <button className="bg-[#CC2E25] w-[100px] text-white rounded py-2 text-xs font-medium transition-all hover:opacity-85">
                    E-PAPER
                  </button>
                </a>
                {!user ? (
                  <Link href="/login" aria-label="Go to signin page">
                    <button className="bg-[#1F1F1F] w-[100px] text-white rounded py-2 text-xs font-medium transition-all hover:opacity-85">
                      Sign In
                    </button>
                  </Link>
                ) : (
                  <div className="relative pl-3">
                    <div
                      className="size-[28px] rounded-full overflow-hidden cursor-pointer"
                      onClick={() => setUserDropdownOpen(true)}
                    >
                      <img
                        src={user?.pictureURL || "/images/avatar.webp"}
                        alt="avatar"
                        className="object-cover w-full h-full"
                      />
                    </div>

                    {userDropdownOpen && <UserDropdown setUserDropdownOpen={setUserDropdownOpen} />}
                  </div>
                )}
              </div>
            </div>

            {/* <div onClick={() => setSearchModalOpen(true)} className="lg:hidden dark:text-white">
              <BsSearch size={27} />
            </div> */}
          </div>
          <div className="flex justify-center pt-1 pb-3 lg:hidden">
            <div className="flex items-center divide-x ">
              {lang === "en" ? (
                <Link
                  href={(process.env.NEXT_PUBLIC_CLIENT_URL as string) || ""}
                  aria-label="Open the Malayalam Newses in a new tab"
                >
                  <p className="px-1 text-sm font-semibold uppercase dark:text-white whitespace-nowrap font-notosans">
                    മലയാളം
                  </p>
                </Link>
              ) : (
                <Link
                  href={(process.env.NEXT_PUBLIC_EN_CLIENT_URL as string) || ""}
                  aria-label="Open the English Newses in a new tab"
                >
                  <p className="px-1 text-xs font-semibold uppercase dark:text-white whitespace-nowrap">
                    English
                  </p>
                </Link>
              )}
              <a href={`/epaper`} target="_blank" aria-label="Open the E-paper in a new tab">
                <p className="px-1 text-xs font-semibold uppercase text-primaryRed whitespace-nowrap">
                  E-paper
                </p>
              </a>
              {/* <a
                href={`https://www.deshabhimani.com/assembly-election`}
                target="_blank"
                aria-label="Open the assembly election page in a new tab"
              >
                <p className="px-1 text-xs font-semibold text-[#0e90c3] uppercase whitespace-nowrap">
                  Assembly Election
                </p>
              </a> */}
            </div>
          </div>
        </div>
      </div>

      <div className="lg:container pt-2">
        <div className="border-t lg:border-t-0 border-b lg:border-black lg:dark:border-[#eee] flex items-center justify-between">
          <div className="hidden lg:flex items-center gap-4">
            <div
              className="bg-[#CC2E25] flex items-center gap-2 text-white px-4 py-2.5 cursor-pointer"
              onClick={() => setHamburgerMenuOpen(true)}
            >
              <MdMenu />
              <span className="text-xs font-semibold">MENU</span>
            </div>
            {/* <Link href="/assembly-election">
              <span className="dark:text-white text-sm font-semibold transition-all hover:text-primaryRed">
                ELECTION SPECIAL
              </span>
            </Link> */}
          </div>

          <div className="flex items-center gap-3 divide-x divide-borderColor lg:divide-black dark:divide-[#eee] dark:text-white overflow-y-auto">
            <Link
              href={`/latest-news`}
              className="text-xs uppercase whitespace-nowrap pl-3 py-3 lg:py-1 transition-all hover:text-primaryRed" 
              aria-label={`Go to latest news page`}
            >
              Latest News
            </Link>
            {mainNavLinks?.map((navLink, index) => (
              <Link
                href={`/${navLink?.slug}`}
                key={index}
                className="text-xs uppercase whitespace-nowrap pl-3 py-3 lg:py-1 transition-all hover:text-primaryRed"
                aria-label={`Go to ${navLink?.name} page`}
              >
                {navLink?.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {hamburgerMenuOpen && (
        <HamburgerMenuModal
          setHamburgerMenuOpen={setHamburgerMenuOpen}
          categories={categories}
          socialLinks={socialLinks}
        />
      )}

      {searchModalOpen && (
        <SearchModal setSearchModalOpen={setSearchModalOpen} socialLinks={socialLinks} />
      )}
    </>
  );
}
